import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Box, Button, Skeleton, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import SportsSoccerIcon from "@mui/icons-material/SportsSoccer";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../supabaseClient";
import {
  fetchAllLeagueTeamsForUser,
  fetchMyRosterRowForTeam,
  resolveOfficialTeamId,
} from "../utils/playerLeagueData";
import { fetchTeamLeagueData, leagueQueryString } from "../utils/teamLeagueData";
import { buildTeamSummary } from "../utils/teamSummary";
import { seasonLabel } from "../utils/leagueCatalog";
import { splitProductName } from "../utils/productDescription";
import { EmptyState, PillChips } from "./ui/Pills";
import TeamCrest from "./ui/TeamCrest";

const ROLE_LABEL = { captain: "Captain", player: "Player", individual: "Individual" };
const RESULT_BG = { W: "#16a34a", D: "#9ca3af", L: "#dc2626", F: "#111" };

const ordinal = (n) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

const dateLabel = (key) => {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d, 12)).toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric", timeZone: "America/Toronto" });
};

const timeLabel = (t) => {
  if (!t) return "";
  const [h, m = "00"] = String(t).split(":");
  return `${Number(h) % 12 || 12}:${m.slice(0, 2)} ${Number(h) >= 12 ? "PM" : "AM"}`;
};

const sameName = (a, b) => String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase();

/** The user's own `players` row on the official team, matched by roster name, then jersey number. */
const findMe = (players, rosterRow) => {
  if (!rosterRow) return null;
  return (
    players.find((p) => sameName(p.name, rosterRow.name)) ||
    (rosterRow.jersey_number != null && rosterRow.jersey_number !== ""
      ? players.find((p) => String(p.jersey_number) === String(rosterRow.jersey_number))
      : null) ||
    null
  );
};

async function loadRegistration(user, lt) {
  const officialId = await resolveOfficialTeamId(lt);
  const [teamData, rosterRow, roster, product] = await Promise.all([
    officialId ? fetchTeamLeagueData(officialId) : Promise.resolve(null),
    fetchMyRosterRowForTeam(user, lt.id),
    lt.role === "individual"
      ? Promise.resolve([])
      : supabase
          .from("league_team_players")
          .select("id, name, email, jersey_number, jersey_size, user_id")
          .eq("team_id", lt.id)
          .order("jersey_number")
          .then(({ data }) => data || []),
    lt.product_id
      ? supabase.from("products").select("name").eq("id", lt.product_id).maybeSingle().then(({ data }) => data)
      : Promise.resolve(null),
  ]);
  const team = teamData?.team ? teamData : null;
  return {
    lt,
    team,
    summary: team ? buildTeamSummary(team.team.id, team.fixtures) : null,
    rosterRow,
    roster,
    me: team ? findMe(team.players, rosterRow) : null,
    programName: product?.name ? splitProductName(product.name).title : null,
  };
}

const Panel = ({ title, action, children, sx }) => (
  <Box component="section" sx={{ bgcolor: "#fff", border: "1px solid #ececec", borderRadius: 3, p: { xs: 2, sm: 2.5 }, ...sx }}>
    {(title || action) && (
      <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1, mb: 1.5 }}>
        <Typography component="h2" sx={{ fontWeight: 900, fontSize: "1.05rem", color: "#111" }}>
          {title}
        </Typography>
        {action}
      </Box>
    )}
    {children}
  </Box>
);

const SmallLink = ({ to, children }) => (
  <Button component={Link} to={to} size="small" sx={{ textTransform: "none", fontWeight: 800, color: "#111", whiteSpace: "nowrap" }}>
    {children}
  </Button>
);

const RoleChip = ({ role }) => (
  <Box
    component="span"
    sx={{
      px: 1.1,
      py: 0.3,
      borderRadius: 999,
      fontSize: "0.7rem",
      fontWeight: 800,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      bgcolor: role === "captain" ? "#111" : "#f1f1f1",
      color: role === "captain" ? "#fff" : "#333",
    }}
  >
    {ROLE_LABEL[role]}
  </Box>
);

const Crest = ({ src, size = 64 }) => <TeamCrest url={src} size={size} />;

const PaymentNote = ({ status }) =>
  status === "paid" ? null : (
    <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 2, bgcolor: "#fff7ed", color: "#9a3412", fontSize: "0.88rem", fontWeight: 600 }}>
      Payment isn&apos;t complete for this registration yet. Your spot is only held once payment goes through.{" "}
      <Box component={Link} to="/register/select-division" sx={{ color: "inherit", fontWeight: 800 }}>
        Finish registering
      </Box>
    </Box>
  );

/** Crest, name, position, form and the user's own numbers for a team they're on. */
const TeamHero = ({ reg }) => {
  const { team, summary, me, lt } = reg;
  const row = summary?.row;
  const qs = leagueQueryString(team.league);
  return (
    <Panel>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Crest src={team.team.logo_url || lt.logo_url} size={72} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <RoleChip role={lt.role} />
            <Typography sx={{ fontSize: "0.72rem", fontWeight: 800, letterSpacing: 1.5, textTransform: "uppercase", color: "rgba(0,0,0,0.45)" }}>
              {[team.league.city, team.league.division, team.league.season && seasonLabel(team.league.season)].filter(Boolean).join(" · ")}
            </Typography>
          </Box>
          <Typography
            component={Link}
            to={`/team/${team.team.id}`}
            sx={{ display: "block", fontSize: { xs: "1.5rem", sm: "1.9rem" }, fontWeight: 900, letterSpacing: "-0.02em", lineHeight: 1.15, color: "#111", textDecoration: "none", mt: 0.5, "&:hover": { textDecoration: "underline" } }}
          >
            {team.team.name}
          </Typography>
          {row && row.gp > 0 ? (
            <Typography sx={{ color: "rgba(0,0,0,0.6)", fontWeight: 600, fontSize: "0.92rem" }}>
              {ordinal(summary.position)} of {summary.teamCount} · {row.points} pts · {row.wins}W {row.draws}D {row.losses}L
            </Typography>
          ) : (
            <Typography sx={{ color: "rgba(0,0,0,0.6)", fontSize: "0.92rem" }}>Season hasn&apos;t started yet.</Typography>
          )}
          {summary?.form.length > 0 && (
            <Box sx={{ display: "flex", gap: 0.5, mt: 0.75 }}>
              {summary.form.map((r, i) => (
                <Box key={i} sx={{ width: 22, height: 22, borderRadius: 1.25, bgcolor: RESULT_BG[r], color: "#fff", fontWeight: 800, fontSize: "0.7rem", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {r}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>
      {me && (
        <Box sx={{ mt: 2, display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1 }}>
          {[
            ["Your goals", me.goals || 0, "#b7860b"],
            ["Assists", me.assists || 0, "#111"],
            ["Red cards", me.red_cards || 0, me.red_cards ? "#dc2626" : "#111"],
          ].map(([label, value, color]) => (
            <Box key={label} sx={{ bgcolor: "#fafafa", borderRadius: 2, px: 1.5, py: 1 }}>
              <Typography sx={{ fontSize: "0.66rem", fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(0,0,0,0.45)" }}>{label}</Typography>
              <Typography sx={{ fontSize: "1.4rem", fontWeight: 900, color }}>{value}</Typography>
            </Box>
          ))}
        </Box>
      )}
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 2 }}>
        <Button component={Link} to={`/schedule?${qs}&team=${team.team.id}`} variant="contained" disableElevation sx={{ textTransform: "none", fontWeight: 800, borderRadius: 999, bgcolor: "#111", "&:hover": { bgcolor: "#000" } }}>
          Our fixtures
        </Button>
        <Button component={Link} to={`/standings?${qs}`} variant="outlined" sx={{ textTransform: "none", fontWeight: 800, borderRadius: 999, color: "#111", borderColor: "rgba(0,0,0,0.2)" }}>
          Standings
        </Button>
        <Button component={Link} to={`/team/${team.team.id}`} variant="outlined" sx={{ textTransform: "none", fontWeight: 800, borderRadius: 999, color: "#111", borderColor: "rgba(0,0,0,0.2)" }}>
          Team page
        </Button>
      </Box>
    </Panel>
  );
};

const NextGame = ({ reg }) => {
  const next = reg.summary?.next;
  if (!next) return null;
  const f = next.fixture;
  return (
    <Box sx={{ bgcolor: "#111", color: "#fff", borderRadius: 3, p: { xs: 2, sm: 2.5 }, display: "flex", alignItems: "center", gap: 2 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: "0.7rem", fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", opacity: 0.6 }}>Next game</Typography>
        <Typography sx={{ fontWeight: 900, fontSize: "1.2rem", mt: 0.25 }}>
          {next.home ? "vs" : "@"} {next.opponent.name || "TBD"}
        </Typography>
        <Typography sx={{ opacity: 0.75, fontSize: "0.88rem" }}>
          {dateLabel(f.date)}
          {f.time ? ` · ${timeLabel(f.time)}` : ""}
        </Typography>
        {f.field && (
          <Typography sx={{ opacity: 0.75, fontSize: "0.85rem", display: "flex", alignItems: "center", gap: 0.4 }}>
            <PlaceOutlinedIcon sx={{ fontSize: 15 }} />
            {f.field}
          </Typography>
        )}
      </Box>
      <Crest src={next.opponent.logo_url} size={56} />
    </Box>
  );
};

const RosterList = ({ players, showContact, highlightName }) => (
  <>
    {players.map((p) => (
      <Box key={p.id || p.name} sx={{ display: "grid", gridTemplateColumns: "34px minmax(0,1fr) auto", gap: 1, alignItems: "center", py: 0.8, borderBottom: "1px solid #f4f4f4", "&:last-of-type": { borderBottom: 0 } }}>
        <Typography sx={{ color: "#999", fontVariantNumeric: "tabular-nums", fontSize: "0.88rem" }}>{p.jersey_number ?? "–"}</Typography>
        <Box sx={{ minWidth: 0 }}>
          <Typography noWrap sx={{ fontWeight: sameName(p.name, highlightName) ? 900 : 600, fontSize: "0.9rem" }}>
            {p.name}
            {sameName(p.name, highlightName) ? " (you)" : ""}
          </Typography>
          {showContact && p.email && (
            <Typography noWrap sx={{ fontSize: "0.75rem", color: "#888" }}>
              {p.email}
            </Typography>
          )}
        </Box>
        {showContact ? (
          <Typography sx={{ fontSize: "0.75rem", color: "#888" }}>{p.jersey_size || ""}</Typography>
        ) : (
          p.goals > 0 && (
            <Typography sx={{ fontWeight: 800, color: "#b7860b", fontSize: "0.88rem", display: "flex", alignItems: "center", gap: 0.4 }}>
              {p.goals} <SportsSoccerIcon sx={{ fontSize: 14 }} />
            </Typography>
          )
        )}
      </Box>
    ))}
  </>
);

const CaptainTools = ({ reg }) => {
  const { roster, lt } = reg;
  const missingNumbers = roster.filter((p) => p.jersey_number == null || p.jersey_number === "").length;
  const missingSizes = roster.filter((p) => !p.jersey_size).length;
  const todo = [
    missingNumbers > 0 && `${missingNumbers} player${missingNumbers === 1 ? "" : "s"} without a jersey number`,
    missingSizes > 0 && `${missingSizes} player${missingSizes === 1 ? "" : "s"} without a jersey size`,
  ].filter(Boolean);
  return (
    <Panel
      title="Captain tools"
      action={
        <Button component={Link} to={`/roster?team_id=${encodeURIComponent(lt.id)}`} variant="contained" disableElevation size="small" sx={{ textTransform: "none", fontWeight: 800, borderRadius: 999, bgcolor: "#111", "&:hover": { bgcolor: "#000" } }}>
          Manage roster
        </Button>
      }
    >
      {todo.length > 0 ? (
        <Box sx={{ mb: 1.5, p: 1.5, borderRadius: 2, bgcolor: "#fafafa" }}>
          <Typography sx={{ fontSize: "0.7rem", fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(0,0,0,0.5)", mb: 0.5 }}>To do</Typography>
          {todo.map((t) => (
            <Typography key={t} sx={{ fontSize: "0.88rem", fontWeight: 600 }}>
              • {t}
            </Typography>
          ))}
        </Box>
      ) : (
        <Typography sx={{ mb: 1.5, fontSize: "0.88rem", color: roster.length ? "#16a34a" : "#666", fontWeight: 700, display: "flex", alignItems: "center", gap: 0.5 }}>
          {roster.length ? <CheckIcon sx={{ fontSize: 18 }} /> : null}
          {roster.length ? `${roster.length} players, all with numbers and sizes` : "Add your players so they can sign in and see the team."}
        </Typography>
      )}
      <Typography sx={{ fontSize: "0.7rem", fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(0,0,0,0.45)", mb: 0.5 }}>
        Roster · only you can see emails
      </Typography>
      {roster.length ? <RosterList players={roster} showContact highlightName={reg.rosterRow?.name} /> : <Typography sx={{ color: "#888" }}>No players added yet.</Typography>}
      <PaymentNote status={lt.payment_status} />
      <Box sx={{ mt: 1.5, mx: -1 }}>
        <SmallLink to={lt.division ? `/photos?${new URLSearchParams({ album: lt.division })}` : "/photos"}>Match photos · view, download, share →</SmallLink>
      </Box>
    </Panel>
  );
};

const Step = ({ done, active, title, body }) => (
  <Box sx={{ display: "flex", gap: 1.5, pb: 2, position: "relative", "&:last-of-type": { pb: 0 } }}>
    <Box
      sx={{
        width: 26,
        height: 26,
        borderRadius: "50%",
        flexShrink: 0,
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: done ? "#16a34a" : active ? "#111" : "#fff",
        border: done || active ? "none" : "2px solid #ddd",
        color: "#fff",
      }}
    >
      {done && <CheckIcon sx={{ fontSize: 16 }} />}
      {active && <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#fff" }} />}
    </Box>
    <Box>
      <Typography sx={{ fontWeight: 800, fontSize: "0.95rem", color: done || active ? "#111" : "#999" }}>{title}</Typography>
      {body && <Typography sx={{ fontSize: "0.85rem", color: "#666" }}>{body}</Typography>}
    </Box>
  </Box>
);

/** Individual still in the player pool: where they are in the process and what happens next. */
const WaitingForTeam = ({ reg }) => {
  const { lt, programName } = reg;
  const paid = lt.payment_status === "paid";
  const division = lt.division ? `${lt.region} · ${lt.division}` : lt.region;
  return (
    <Panel>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        <RoleChip role="individual" />
        <Typography sx={{ fontSize: "0.72rem", fontWeight: 800, letterSpacing: 1.5, textTransform: "uppercase", color: "rgba(0,0,0,0.45)" }}>
          {[division, lt.season && seasonLabel(lt.season)].filter(Boolean).join(" · ")}
        </Typography>
      </Box>
      <Typography component="h2" sx={{ fontSize: { xs: "1.5rem", sm: "1.9rem" }, fontWeight: 900, letterSpacing: "-0.02em", lineHeight: 1.15, color: "#111", mt: 0.5 }}>
        {programName || `${lt.division || ""} individual registration`}
      </Typography>
      <Typography sx={{ color: "rgba(0,0,0,0.6)", mt: 0.5, mb: 2.5, fontSize: "0.92rem" }}>
        You signed up on your own. We build teams from the individual pool and you&apos;ll see your team, teammates and fixtures here as soon as you&apos;re placed.
      </Typography>
      <Step done title="Registered" body={lt.created_at ? `Signed up ${new Date(lt.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}` : null} />
      <Step done={paid} active={!paid} title={paid ? "Payment received" : "Payment pending"} body={paid ? null : "Finish payment to hold your spot."} />
      <Step active={paid} title="Team placement" body="The league places individuals on teams and you'll see yours here." />
      <Step title="First game" body="Your fixtures appear here once you're on a team." />
      <PaymentNote status={lt.payment_status} />
      {lt.division && (
        <Box sx={{ mt: 2 }}>
          <SmallLink to={`/schedule?${new URLSearchParams({ city: lt.region, division: lt.division })}`}>See the {lt.division} schedule →</SmallLink>
        </Box>
      )}
    </Panel>
  );
};

const RegistrationView = ({ reg }) => {
  const { lt, team, roster } = reg;
  if (lt.role === "individual" && !team) return <WaitingForTeam reg={reg} />;

  if (!team) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Panel>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Crest src={lt.logo_url} size={64} />
            <Box>
              <RoleChip role={lt.role} />
              <Typography sx={{ fontSize: { xs: "1.5rem", sm: "1.9rem" }, fontWeight: 900, color: "#111", mt: 0.5 }}>{lt.name}</Typography>
              <Typography sx={{ color: "rgba(0,0,0,0.6)", fontSize: "0.92rem" }}>
                {[lt.region, lt.division, lt.season && seasonLabel(lt.season)].filter(Boolean).join(" · ")}
              </Typography>
            </Box>
          </Box>
          <Typography sx={{ mt: 2, color: "rgba(0,0,0,0.65)", fontSize: "0.92rem" }}>
            Your team is registered. Fixtures, the table and stats show up here once the schedule is published.
          </Typography>
        </Panel>
        {lt.role === "captain" ? (
          <CaptainTools reg={reg} />
        ) : (
          <Panel title="Teammates">
            <RosterList players={roster} highlightName={reg.rosterRow?.name} />
          </Panel>
        )}
      </Box>
    );
  }

  const squad = team.players.length ? team.players : roster;
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "3fr 2fr" }, gap: 2, alignItems: "start" }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
        {lt.role === "individual" && (
          <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: "#ecfdf3", color: "#166534", fontWeight: 700, fontSize: "0.9rem", display: "flex", gap: 1, alignItems: "center" }}>
            <CheckIcon sx={{ fontSize: 18 }} /> You signed up as an individual and were placed on {team.team.name}.
          </Box>
        )}
        <TeamHero reg={reg} />
        <NextGame reg={reg} />
      </Box>
      {lt.role === "captain" ? (
        <CaptainTools reg={reg} />
      ) : (
        <Panel title="Teammates" action={<SmallLink to={`/team/${team.team.id}`}>Squad stats →</SmallLink>}>
          {squad.length ? (
            <RosterList
              players={[...squad].sort((a, b) => (Number(a.jersey_number) || 999) - (Number(b.jersey_number) || 999))}
              highlightName={reg.me?.name || reg.rosterRow?.name}
            />
          ) : (
            <Typography sx={{ color: "#888", fontSize: "0.9rem" }}>Roster not published yet.</Typography>
          )}
          {lt.role === "player" && (
            <Typography sx={{ mt: 1.5, fontSize: "0.78rem", color: "#888" }}>Your captain manages the roster. Ask them for any changes.</Typography>
          )}
        </Panel>
      )}
    </Box>
  );
};

const headline = (reg) => {
  if (!reg) return "";
  const { lt, team } = reg;
  if (lt.role === "captain") return `You captain ${team?.team.name || lt.name}.`;
  if (lt.role === "player") return `You play for ${team?.team.name || lt.name}.`;
  return team ? `You play for ${team.team.name}.` : "Your individual registration is in. We'll place you on a team.";
};

/** Signed-in home: one view per registration, shaped by whether the user is a captain, a rostered player or an individual. */
const PlayerHome = () => {
  const { user } = useAuth();
  const [regs, setRegs] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    (async () => {
      const list = await fetchAllLeagueTeamsForUser(user);
      const loaded = await Promise.all(list.map((lt) => loadRegistration(user, lt)));
      if (!cancelled) setRegs(loaded);
    })().catch((e) => {
      console.error("Player home:", e);
      if (!cancelled) setRegs([]);
    });
    return () => {
      cancelled = true;
    };
  }, [user]);

  const current = useMemo(() => (regs || []).find((r) => r.lt.id === selected) || regs?.[0] || null, [regs, selected]);
  const firstName = String(user?.user_metadata?.full_name || current?.rosterRow?.name || "").trim().split(/\s+/)[0];

  if (regs === null) {
    return (
      <Box sx={{ maxWidth: 1080, mx: "auto", px: { xs: 2, sm: 3 }, py: 4 }}>
        <Skeleton width={120} />
        <Skeleton width={280} height={48} />
        <Skeleton variant="rounded" height={220} sx={{ mt: 3, borderRadius: 3 }} />
      </Box>
    );
  }

  if (!regs.length) {
    return <EmptyState title="No registrations on this account" body="Sign up for a league as a team or on your own." action={{ label: "Register", to: "/register/select-division" }} sx={{ mt: 6 }} />;
  }

  return (
    <Box sx={{ maxWidth: 1080, mx: "auto", px: { xs: 2, sm: 3 }, pt: { xs: 3, sm: 4 }, pb: 6, minHeight: "70vh" }}>
      <Typography sx={{ fontSize: "0.75rem", fontWeight: 800, letterSpacing: 2, textTransform: "uppercase", color: "rgba(0,0,0,0.45)" }}>My HSL</Typography>
      <Typography component="h1" sx={{ fontSize: { xs: "2rem", sm: "2.6rem" }, fontWeight: 900, letterSpacing: "-0.03em", lineHeight: 1.1, color: "#111" }}>
        {firstName ? `Hey ${firstName}` : "Welcome back"}
      </Typography>
      <Typography sx={{ color: "rgba(0,0,0,0.6)", mt: 0.5 }}>{headline(current)}</Typography>

      {regs.length > 1 && (
        <PillChips
          ariaLabel="Your registrations"
          value={current.lt.id}
          onChange={setSelected}
          sx={{ mt: 2 }}
          options={regs.map((r) => ({
            value: r.lt.id,
            label: `${r.team?.team.name || (r.lt.role === "individual" ? `${r.lt.division} (individual)` : r.lt.name)} · ${ROLE_LABEL[r.lt.role]}`,
          }))}
        />
      )}

      <Box sx={{ mt: 3 }}>
        <RegistrationView reg={current} />
      </Box>
    </Box>
  );
};

export default PlayerHome;
